import { useEffect, useRef, useState } from 'react';

type LineKind = 'inbound' | 'action' | 'result';

interface LedgerLine {
  time: string;
  kind: LineKind;
  msg: React.ReactNode;
  src?: string;
}

/** One overnight shift, drawn from what Pulse + Lumen actually do. */
const HERO_LINES: LedgerLine[] = [
  {
    time: '02:14',
    kind: 'inbound',
    msg: 'WhatsApp · +•• ••• •••• 41: "Do you have the SPX-200 infusion pump in stock?"',
  },
  {
    time: '02:14',
    kind: 'action',
    msg: (
      <>
        agent <b>searched knowledge</b>
      </>
    ),
    src: 'catalog-2026.pdf · p.14',
  },
  {
    time: '02:14',
    kind: 'result',
    msg: 'replied: "In stock — 12 units. Want a formal quote?"',
  },
  {
    time: '02:15',
    kind: 'action',
    msg: (
      <>
        agent <b>tagged contact</b> · warm-lead
      </>
    ),
  },
  {
    time: '02:15',
    kind: 'action',
    msg: (
      <>
        agent <b>drafted quote</b> in Lumen ERP
      </>
    ),
  },
  {
    time: '02:15',
    kind: 'result',
    msg: 'follow-up scheduled · today 09:00',
  },
];

const EXPANDED_LINES: LedgerLine[] = [
  ...HERO_LINES,
  {
    time: '03:02',
    kind: 'inbound',
    msg: 'WhatsApp · +•• ••• •••• 88: "What are your delivery terms for the EU?"',
  },
  {
    time: '03:02',
    kind: 'action',
    msg: (
      <>
        agent <b>searched knowledge</b>
      </>
    ),
    src: 'shipping-terms.pdf · p.3',
  },
  {
    time: '03:02',
    kind: 'result',
    msg: 'replied with terms · conversation resolved, no human needed',
  },
];

interface AgentLedgerProps {
  variant?: 'hero' | 'expanded';
  title?: string;
}

/**
 * The signature element: a typed agent-activity console. Lines appear
 * with staggered timing once the ledger scrolls into view. Under
 * prefers-reduced-motion the full transcript renders statically.
 */
export function AgentLedger({
  variant = 'hero',
  title = 'Agent activity — overnight',
}: AgentLedgerProps): React.JSX.Element {
  const lines = variant === 'expanded' ? EXPANDED_LINES : HERO_LINES;
  const ref = useRef<HTMLDivElement>(null);

  const prefersStatic =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const [shown, setShown] = useState(prefersStatic ? lines.length : 0);
  const [started, setStarted] = useState(prefersStatic);

  // Start typing when the ledger enters the viewport.
  useEffect(() => {
    if (started) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setStarted(true);
          io.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [started]);

  // Reveal lines one by one.
  useEffect(() => {
    if (!started || prefersStatic || shown >= lines.length) return;
    const delay = shown === 0 ? 400 : lines[shown]?.kind === 'inbound' ? 1100 : 700;
    const t = window.setTimeout(() => setShown((n) => n + 1), delay);
    return () => window.clearTimeout(t);
  }, [started, prefersStatic, shown, lines]);

  const done = shown >= lines.length;

  return (
    <div
      ref={ref}
      className={`ledger ${variant === 'expanded' ? 'expanded' : ''} ${prefersStatic ? 'static' : ''}`}
      role="img"
      aria-label="Example transcript of an AI agent answering a WhatsApp inquiry, citing a PDF, tagging the contact, drafting a quote in Lumen ERP, and scheduling a follow-up."
    >
      <div className="ledger-head" aria-hidden="true">
        <span className="ledger-title">{title}</span>
        <span className="ledger-live">no humans awake</span>
      </div>
      <div className="ledger-body" aria-hidden="true">
        {lines.map((line, i) => (
          <div
            key={i}
            className={`ledger-line ${line.kind} ${i < shown ? 'shown' : ''} ${
              !done && i === shown - 1 ? 'typing' : ''
            }`}
          >
            <span className="ledger-time">{line.time}</span>
            <span className="ledger-msg">
              {line.msg}
              {line.src && <span className="ledger-src">source: {line.src}</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
